import { element } from "./dom.js";
import { saveMarks } from "./segmenter-save-marks.js";
import type { SegmentedWord } from "../segmenting/segmented-words.js";
import {
  saveWordOffer,
  signUpLinkClass,
  signUpRoute,
} from "../user/sign-up-offer.js";

/**
 * The control that keeps one word of the list.
 *
 * The classes are `src/site/save-word.ts`'s contract and are the same ones a
 * 词典 entry and `/user/ci/` carry. `modules/save-word` draws all three.
 *
 * **This one says its two states in marks rather than in words.** `Save` and
 * `Saved` under a Save column, once per row over a few hundred rows, is the
 * widest column in the table saying what the heading above it already said. A
 * plus and a bin are the two acts, the bin being what a reader is actually
 * offered on a word they already hold.
 *
 * The word stays in the markup as the `.cb-save-word-label` span, hidden. That
 * element is what `drawSavedWord` writes to when a press succeeds, and taking
 * it out would leave the shared script with nowhere to put its answer. Nothing
 * is lost by hiding it. `aria-label` on the button is its whole accessible
 * name, and it names the word as well as the act.
 */

const ciPath = "/user/ci/";

/** What one control needs told. */
export interface SegmenterSaveControlProperties {
  readonly word: SegmentedWord;

  /** Whether the reader already holds the word. */
  readonly held: boolean;

  /**
   * Where the icon sprite is, which the template carries.
   *
   * Absent on a page whose markup predates it, and the control then draws its
   * label rather than its marks. `modules/save-word` already has the ✓ a page
   * with no sprite falls back to.
   */
  readonly icons?: string | undefined;
}

/** A save control for a segmented word with a dictionary entry. */
export function segmenterSaveControl(
  properties: SegmenterSaveControlProperties,
): HTMLElement | undefined {
  const { word, held, icons } = properties;

  if (word.headword === undefined) {
    return undefined;
  }

  const button = element("button", {
    className: "cb-save-word-action",
    attributes: {
      type: "submit",
      name: "word",
      value: word.headword,
      "aria-label": held
        ? `Saved ${word.text}, press to remove`
        : `Save ${word.text}`,
      "data-cb-word-label": word.text,
      "data-cb-saved": held ? "yes" : undefined,
    },
    children: [
      ...saveMarks(icons),
      element("span", {
        className: `cb-save-word-label${icons === undefined ? "" : " visually-hidden"}`,
        text: held ? "Saved" : "Save",
      }),
    ],
  });

  return element("div", {
    className: "cb-save-word cb-segmenter-save-word",
    children: [
      element("form", {
        className: "cb-save-word-form",
        attributes: { method: "post", action: ciPath },
        children: [button],
      }),
      element("p", {
        className: "cb-save-word-offer",
        attributes: { hidden: "" },
        children: [
          element("a", {
            className: signUpLinkClass,
            attributes: { href: signUpRoute },
            text: saveWordOffer,
          }),
        ],
      }),
    ],
  });
}
